/**
 * Sort Utilities for DataTable
 * 
 * Handles sorting of table urolumns with mixed data types:
 * - Numbers (with comma/dot separators)
 * - Booleans (true/false)
 * - Text (strings)
 * - N/A values (treated as smallest)
 */

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

/**
 * Check if a value is considered "empty" or N/A
 */
export function isNullOrNA(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '' || normalized === 'n/a' || normalized === 'na' || normalized === '-';
  }
  return false;
}

/**
 * Parse a string or number into a numeric value.
 * Handles various formats:
 * - "1,234,567" (comma as thousands separator)
 * - "1.234.567" (dot as thousands separator, European)
 * - "1,234.56" (comma thousands, dot decimal)
 * - "1.234,56" (European: dot thousands, comma decimal)
 * - "1234567" (no separator)
 * - "-1,234.56" (negative numbers)
 * 
 * Returns null if parsing fails.
 */
export function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  // Check for N/A first
  if (isNullOrNA(trimmed)) return null;

  // Remove currency symbols and whitespace
  let cleaned = trimmed.replace(/[$€£¥₹\s]/g, '');

  // Handle percentage
  const isPercent = cleaned.endsWith('%');
  if (isPercent) {
    cleaned = cleaned.slice(0, -1);
  }

  // Count separators to determine format
  const commaCount = (cleaned.match(/,/g) || []).length;
  const dotCount = (cleaned.match(/\./g) || []).length;

  // Determine which is decimal and which is thousands separator
  if (commaCount > 0 && dotCount > 0) {
    // Both separators present - determine by position
    const lastCommaIndex = cleaned.lastIndexOf(',');
    const lastDotIndex = cleaned.lastIndexOf('.');

    if (lastDotIndex > lastCommaIndex) {
      // Format: 1,234.56 (US style - comma is thousands, dot is decimal)
      cleaned = cleaned.replace(/,/g, '');
    } else {
      // Format: 1.234,56 (European style - dot is thousands, comma is decimal)
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
  } else if (commaCount === 1) {
    // Single comma - could be thousands (1,234) or decimal (1,5)
    const parts = cleaned.split(',');
    if (parts[1] && parts[1].length === 3 && !parts[1].includes('.')) {
      // Likely thousands separator (1,234 -> 1234)
      cleaned = cleaned.replace(',', '');
    } else {
      // Likely decimal separator (1,5 -> 1.5)
      cleaned = cleaned.replace(',', '.');
    }
  } else if (commaCount > 1) {
    // Multiple commas - definitely thousands separators (1,234,567)
    cleaned = cleaned.replace(/,/g, '');
  } else if (dotCount > 1) {
    // Multiple dots - definitely thousands separators (1.234.567)
    cleaned = cleaned.replace(/\./g, '');
  }
  // Single dot or no separators - already in correct format

  const parsed = parseFloat(cleaned);
  
  if (isNaN(parsed)) return null;
  
  // Convert percentage back to decimal if needed (optional - keep as-is for sorting)
  return parsed;
}

/**
 * Parse a date string in "yyyy-mm" or "yyyy-mm-dd" format
 * Returns a comparable numeric value (timestamp) or null if parsing fails
 */
export function parseDateString(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  
  const trimmed = value.trim();
  if (trimmed === '' || isNullOrNA(trimmed)) return null;

  // Match yyyy-mm format (e.g., "2025-04", "2024-12")
  const yearMonthMatch = trimmed.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const year = parseInt(yearMonthMatch[1], 10);
    const month = parseInt(yearMonthMatch[2], 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12) {
      // Return a comparable value: year * 100 + month (e.g., 202504)
      return year * 100 + month;
    }
  }

  // Match yyyy-mm-dd format (e.g., "2025-04-15")
  const fullDateMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (fullDateMatch) {
    const year = parseInt(fullDateMatch[1], 10);
    const month = parseInt(fullDateMatch[2], 10);
    const day = parseInt(fullDateMatch[3], 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      // Return a comparable value: year * 10000 + month * 100 + day (e.g., 20250415)
      return year * 10000 + month * 100 + day;
    }
  }

  // Match mm/dd/yyyy or dd/mm/yyyy is ambiguous, skip for now
  // Match yyyy/mm/dd format
  const slashDateMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (slashDateMatch) {
    const year = parseInt(slashDateMatch[1], 10);
    const month = parseInt(slashDateMatch[2], 10);
    const day = parseInt(slashDateMatch[3], 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return year * 10000 + month * 100 + day;
    }
  }

  return null;
}

/**
 * Parse a boolean value from various representations
 */
export function parseBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === 'yes' || normalized === '1') return true;
    if (normalized === 'false' || normalized === 'no' || normalized === '0') return false;
  }
  
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  
  return null;
}

/**
 * Detect the predominant data type in a column
 */
export function detectColumnType(values: unknown[]): 'number' | 'boolean' | 'string' | 'date' {
  let numberCount = 0;
  let booleanCount = 0;
  let dateCount = 0;
  let validCount = 0;

  for (const value of values) {
    if (isNullOrNA(value)) continue;
    
    validCount++;
    
    // Check date first (before number, since dates can look like numbers)
    if (parseDateString(value) !== null) {
      dateCount++;
    }
    
    if (parseNumber(value) !== null) {
      numberCount++;
    }
    
    if (parseBoolean(value) !== null) {
      booleanCount++;
    }
  }

  // If no valid values, default to string
  if (validCount === 0) return 'string';

  // Prioritize date detection (yyyy-mm format is very specific)
  // A column is date if most values parse as dates
  if (dateCount >= validCount * 0.8) return 'date';

  // Number detection - a column is numeric if most values parse as numbers
  // But exclude if it's already detected as date
  if (numberCount >= validCount * 0.8 && dateCount < validCount * 0.5) return 'number';
  
  // Boolean detection - must be very consistent
  if (booleanCount >= validCount * 0.9) return 'boolean';
  
  return 'string';
}

/**
 * Compare two values for sorting, treating N/A as smallest
 */
function compareValues(
  a: unknown,
  b: unknown,
  columnType: 'number' | 'boolean' | 'string' | 'date',
  direction: 'asc' | 'desc'
): number {
  const aIsNull = isNullOrNA(a);
  const bIsNull = isNullOrNA(b);

  // Handle N/A values - always treat as smallest
  if (aIsNull && bIsNull) return 0;
  if (aIsNull) return 1; // a goes to bottom (is "smaller")
  if (bIsNull) return -1; // b goes to bottom

  let comparison = 0;

  switch (columnType) {
    case 'date': {
      const aDate = parseDateString(a);
      const bDate = parseDateString(b);
      
      // If parsing fails, fall back to string comparison
      if (aDate === null && bDate === null) {
        comparison = String(a).localeCompare(String(b));
      } else if (aDate === null) {
        comparison = 1; // non-parseable goes to bottom
      } else if (bDate === null) {
        comparison = -1;
      } else {
        comparison = aDate - bDate;
      }
      break;
    }

    case 'number': {
      const aNum = parseNumber(a);
      const bNum = parseNumber(b);
      
      // If parsing fails, fall back to string comparison
      if (aNum === null && bNum === null) {
        comparison = String(a).localeCompare(String(b));
      } else if (aNum === null) {
        comparison = 1; // non-parseable goes to bottom
      } else if (bNum === null) {
        comparison = -1;
      } else {
        comparison = aNum - bNum;
      }
      break;
    }

    case 'boolean': {
      const aBool = parseBoolean(a);
      const bBool = parseBoolean(b);
      
      if (aBool === null && bBool === null) {
        comparison = String(a).localeCompare(String(b));
      } else if (aBool === null) {
        comparison = 1;
      } else if (bBool === null) {
        comparison = -1;
      } else {
        // true > false in ascending order
        comparison = (aBool === bBool) ? 0 : (aBool ? 1 : -1);
      }
      break;
    }

    case 'string':
    default: {
      const aStr = String(a ?? '').toLowerCase();
      const bStr = String(b ?? '').toLowerCase();
      comparison = aStr.localeCompare(bStr);
      break;
    }
  }

  return direction === 'asc' ? comparison : -comparison;
}

/**
 * Sort an array of row objects by a specific column
 */
export function sortTableData<T extends Record<string, unknown>>(
  data: T[],
  sortState: SortState
): T[] {
  const { column, direction } = sortState;

  // No sorting if no column or direction
  if (!column || !direction) {
    return data;
  }

  // Extract values for type detection
  const columnValues = data.map(row => row[column]);
  const columnType = detectColumnType(columnValues);

  // Create a stable sort by preserving original indices for equal values
  const indexed = data.map((row, index) => ({ row, index }));

  indexed.sort((a, b) => {
    const comparison = compareValues(
      a.row[column],
      b.row[column],
      columnType,
      direction
    );
    
    // Stable sort: if values are equal, maintain original order
    if (comparison === 0) {
      return a.index - b.index;
    }
    
    return comparison;
  });

  return indexed.map(item => item.row);
}

/**
 * Toggle sort direction: null -> 'asc' -> 'desc' -> null
 */
export function getNextSortDirection(current: SortDirection): SortDirection {
  switch (current) {
    case null: return 'asc';
    case 'asc': return 'desc';
    case 'desc': return null;
  }
}

/**
 * Get sort state after clicking a column header
 */
export function handleColumnSort(
  currentState: SortState,
  clickedColumn: string
): SortState {
  if (currentState.column === clickedColumn) {
    // Same column - toggle direction
    const nextDirection = getNextSortDirection(currentState.direction);
    return {
      column: nextDirection ? clickedColumn : null,
      direction: nextDirection
    };
  } else {
    // Different column - start with ascending
    return {
      column: clickedColumn,
      direction: 'asc'
    };
  }
}

